import {
  isAURegionSelector,
  isUSRegionSelector,
  useConfigStore,
  useRegionStore,
} from '@packages/stores';
import { getDefaultPDSLink, getRegionDateTime } from '@packages/utils';

import { Assets } from '@assets';
import { Container, EmailQuoteButton } from '@components';
import { Text } from '@elements/text';
import { TextWithMd } from '@elements/text/textWithMd';
import { useNavigateToFreely } from '@hooks';
import { i18n } from '@i18n';
import { testProps } from '@utils';

import { version } from '../../../package.json';

export const Footer = ({ isNotFound = false }: { isNotFound?: boolean }) => {
  const isUS = useRegionStore(isUSRegionSelector);
  const isAU = useRegionStore(isAURegionSelector);

  const config = useConfigStore(state => state.config);

  const pdsLink =
    useConfigStore(state => state?.regionSpecificConfig?.PDS.CURRENT) ?? getDefaultPDSLink();
  const privacyLink = useConfigStore(state => state?.regionSpecificConfig?.PRIVACY_STATEMENT);
  const targetMarketLink = useConfigStore(state => state?.regionSpecificConfig?.TARGET_MARKET); // AU only
  const termsOfUseLink = useConfigStore(state => state?.regionSpecificConfig?.APP_TERMS);
  const fraudLink = useConfigStore(state => state?.regionSpecificConfig?.FRAUD_WARNING); // AU only

  const { openRedirectToFreelyModal, navigateToFreelyWebsite } = useNavigateToFreely();

  const onFreelyIconClick = () =>
    isNotFound ? navigateToFreelyWebsite() : openRedirectToFreelyModal();

  return (
    <footer className="mt-auto w-full relative ">
      <Text variant="footnote-12/r" className="absolute bottom-0 right-2 text-dark-surface">
        v{version}
      </Text>
      <div className="bg-fuji-800 ">
        <Container className="bg-fuji-800 flex flex-row justify-between py-s16 sm:py-s24 ">
          <button onClick={onFreelyIconClick}>
            <Assets.FreelyPrime className="w-16 h-8 sm:h-10 sm:w-24  " fill={'white'} />
          </button>
          <div className="flex flex-row items-center">
            <Text variant="body-16/r" className="invisible mr-5 text-dark-surface md:visible">
              {i18n.t('footer.planetSlogan')}
            </Text>
            <Assets.OnePercentPlanet className="w-16 h-8 sm:h-10 sm:w-24 " fill={'white'} />
          </div>
        </Container>
      </div>
      <div className="bg-fuji-800 text-center">
        <Container>
          {isAU && (
            <div className="mx-auto lg:w-[770px] md:w-[520px] w-auto flex-shrink-0 p-3">
              <Text
                variant={{
                  sm: 'body-16/sb',
                  md: 'body-16/sb',
                  lg: 'subTitle-20/sb',
                }}
                className="text-dark-surface">
                {i18n.t('footer.downloadAppHeading')}
              </Text>

              <div className="mx-auto w-[288px] flex-shrink-0 p-3 mb-7">
                <div className="flex w-full justify-between px-4">
                  <a href={config?.APP_STORE_LINK} rel="noreferrer" className="p-1">
                    <Assets.AppStoreBadge className="h-[39px] " />
                  </a>
                  <a href={config?.PLAY_STORE_LINK} rel="noreferrer" className="p-1">
                    <Assets.GooglePlayBadge className="h-[39px]" />
                  </a>
                </div>
              </div>
            </div>
          )}
          <Text variant="footnote-12/r" className="text-dark-surface">
            <a
              href={pdsLink}
              target="_blank"
              rel="noreferrer"
              {...testProps('product-Disclosure-Statement')}>
              {i18n.t('footer.menuItems.item1')}
            </a>
            <a
              href={isUS ? privacyLink : targetMarketLink}
              target="_blank"
              rel="noreferrer"
              {...testProps('target-Market-Determination')}>
              {i18n.t('footer.menuItems.item2')}
            </a>
            <a
              href={isUS ? termsOfUseLink : privacyLink}
              target="_blank"
              rel="noreferrer"
              {...testProps('privacy-Statement')}>
              {i18n.t('footer.menuItems.item3')}
            </a>
            <a
              href={isUS ? fraudLink : termsOfUseLink}
              target="_blank"
              rel="noreferrer "
              {...testProps('Terms-of-Use')}>
              {i18n.t('footer.menuItems.item4')}
            </a>
            <EmailQuoteButton type="link" />
          </Text>
          <br />
          <Text variant="footnote-12/r" className="text-dark-surface">
            {i18n.t('footer.copyright', { year: getRegionDateTime(isAU ? 'AU' : 'US').year })}
          </Text>
          <br />
          <TextWithMd variant="footnote-12/r" className="text-dark-surface pb-s16">
            {i18n.t('footer.terms')}
          </TextWithMd>
        </Container>
      </div>
    </footer>
  );
};
